import Base from '../../../ts/_base';
import slideScreen from './_slide-screen';
import SliderTemplate from '../templates//multi-level-slide-menu/multi-level-slide-menu-client.jade';


export default class Slider extends Base {
    private screens: slideScreen[];
    private activeScreensStack: string[];
    public id: string;

    constructor(id: string) {
        super();
        this.screens = [];
        this.activeScreensStack = [];
        this.id = id;
        this.ElmRef = this.queryElm(`#slider-${id}`) as HTMLElement;
        if(!this.ElmRef) 
            this.initEmptySlider();
    }


    get slideWidth(): number {
        return this.ElmRef && this.ElmRef.clientWidth;
    }

    get isToggled(): boolean {
        return this.ElmRef && this.ElmRef.classList.contains(this.classList.sliderToggled);
    }

    get classList() {
        return {
            sliderToggled: 'toggled',
            rootScreen: 'slider__screen--root',
            activeScreen: 'active'
        }
    }

    get events() {
        const prefix = 'slider.';
        return {
            sliderToggled: `${prefix}show`,
            sliderUntoggled: `${prefix}hide`,
        }
    }
    
    get selectors() {
        return {
            screen: '.slider__screen',
            sliderContainer: '.slider__container',
            navbar: '.navbar',
            backControl: `[data-slider="${this.id}"][data-action="back"]`,
            navigateControl: `[data-slider="${this.id}"][data-action="navigate"]`,
            toggleControl: `[data-slider="${this.id}"][data-action="toggle"]`
        }
    }

    get rootScreen(): slideScreen {
        return this.screens.find(s => s.isRoot) || this.screens[0];
    }

    registerScreen(slideMenu: slideScreen): slideScreen {
        this.screens.push(slideMenu);
        slideMenu.attachToSlider(this.id);
        return slideMenu;
    }

    build(): void {
        const sliderContainer = this.queryElm(this.selectors.sliderContainer, false, this.ElmRef) as HTMLElement;
        this.screens
        .map(s => s.render())
        .map(html => this.stringToNode(html))
        .forEach(n => sliderContainer.append(n));

        //include all screens
        (this.screens as any) = [...this.queryElm(this.selectors.screen, true, sliderContainer) as any].map((s) => {
            return {
                id: s.id, 
                isRoot: s.classList.contains(this.classList.rootScreen)
            };
        });

        this.registerControls();
    }

    async toggle(): Promise<void> {
        if(!this.ElmRef) return;

        if(this.isToggled) {
            const styles = window.getComputedStyle(this.ElmRef);
            const transitionDuration = eval(styles.transitionDuration.replace('s', '*1000'));
            this.ElmRef.classList.remove(this.classList.sliderToggled);
            await this.wait(transitionDuration);
            this.ElmRef.style.display = 'none';
            this.activeScreensStack = [];
        } else {
            this.ElmRef.style.display = 'block';
            await this.navigateToScreen(this.rootScreen && this.rootScreen.id);
            this.ElmRef.classList.add(this.classList.sliderToggled);
        }
        this.toggleLockScroll()
    }


    async navigateToScreen(screenId: string): Promise<void> {
        const screen = this.queryElm(`#${screenId}`, null, this.ElmRef) as HTMLElement;
        screen.classList.add(this.classList.activeScreen);
        this.activeScreensStack.push(screenId);
        this.reorderScreens();
        await this.slideToActive();
    }

    reorderScreens(): void {
        if(this.activeScreensStack.length === 0) return;
        this.activeScreensStack.forEach((screenId, index) => {
            const screen = this.queryElm(`#${screenId}`, null, this.ElmRef) as HTMLElement;
            screen.style.order = `${index + 1}`; 
        })
    }
    back(): void {
        if(this.activeScreensStack.length === 0) return;
        if(this.activeScreensStack.length === 1) {
            this.toggle();
            return;
        }
        const lastPushedScreen = this.activeScreensStack.pop();
        const screen = this.queryElm(`#${lastPushedScreen}`, null, this.ElmRef) as HTMLElement;
        this.slideToActive().then(() => {
            screen.classList.remove(this.classList.activeScreen);
        });
    }

    slideToActive(): Promise<void> {
        const slideDistance = (this.activeScreensStack.length-1) * this.slideWidth;
        const sliderContainer = this.queryElm(this.selectors.sliderContainer, false, this.ElmRef) as HTMLElement;
        const styles = window.getComputedStyle(sliderContainer);
        const transitionDuration = eval(styles.transitionDuration.replace('s', '*1000'));
        sliderContainer.style.transform = `translateX(-${slideDistance}px)`;
        return this.wait(transitionDuration);
    }

    registerControls(): void {
        this.subscribeToEvent('click', this.selectors.toggleControl, (e) => {
            const { currentTarget } = e;
            if(this.isToggled) {
                currentTarget.setAttribute('aria-expanded', 'false');
                this.triggerEvent(this.events.sliderUntoggled);
            } else {
                currentTarget.setAttribute('aria-expanded', 'true');
                this.triggerEvent(this.events.sliderToggled);
            }
            this.toggle();
        });
        
        this.subscribeToEvent('click', this.selectors.backControl, () => this.back());
        this.subscribeToEvent('click', this.selectors.navigateControl, (e) => {
            const { currentTarget } = e;
            const screenId = currentTarget.getAttribute('data-screen');
            this.navigateToScreen(screenId);
        });
        this.subscribeToEvent('keydown', (e) => {
            const { key } = e;
            if(key !== 'Escape' || !this.isToggled) return;
            const buttonTrigger = this.queryElm(`[aria-controls="#slider-${this.id}"]`) as HTMLElement;
            buttonTrigger.setAttribute('aria-expanded', 'false');
            this.triggerEvent(this.events.sliderUntoggled);
            this.toggle();
        });
    }
    
    initEmptySlider(): void {
        const sliderHTML = SliderTemplate({data: {sliderId: this.id}});
        this.ElmRef = this.stringToNode(sliderHTML);
        (this.queryElm('body') as HTMLElement).append(this.ElmRef);
    }

}
